import React, { ReactNode, forwardRef, useState } from 'react';

import { useCombobox } from 'downshift';

import I18n from '~/src/common/services/I18n';

import {
  Menu,
  Loading,
  ResultFooterContainer,
  ErrorMessage,
  Container,
  StyledInput,
  ClearIcon,
} from './layout';

interface Props<T> extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  value: string;
  placeholder?: string;
  items: T[];
  loading: boolean;
  disabled?: boolean;
  error?: string;
  onChange: (value: string) => Promise<void> | void;
  onSelectItem: (item: T) => Promise<void> | void;
  onClear?: () => void;
  itemToString: (item: T | null) => string;
  renderItem: (item: T, isHighlighted: boolean) => ReactNode;
  ResultFooter?: ReactNode;
  LeftIcon?: ReactNode;
  RightIcon?: ReactNode;
}

const ERROR_MESSAGE_ID = 'NO_STREET_NUMBER';

const AutocompleteInput = <T,>(
  {
    value,
    placeholder,
    disabled,
    loading,
    error,
    items,
    onSelectItem,
    onChange,
    onFocus,
    onBlur,
    onClear,
    itemToString,
    renderItem,
    ResultFooter,
    LeftIcon,
    RightIcon,
    ...inputProps
  }: Props<T>,
  ref: React.Ref<HTMLInputElement>,
) => {
  // Contrôle manuel de l'ouverture du menu
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { getMenuProps, getItemProps, getInputProps, highlightedIndex } = useCombobox({
    items,
    itemToString,
    defaultHighlightedIndex: 0,
    inputValue: value,
    isOpen: isMenuOpen && (value.length > 0 || loading),
    onInputValueChange: changes => {
      void onChange?.(changes?.inputValue ?? '');
      setIsMenuOpen(true);
    },
    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem) {
        void onSelectItem(selectedItem);
        setIsMenuOpen(false);
      }
    },
  });

  const isClearable = value !== '' && !loading && !disabled && onClear;

  return (
    <Container>
      {/* ICONE A GAUCHE DE L'INPUT */}
      {LeftIcon ? LeftIcon : null}

      {/* INPUT */}
      <StyledInput
        {...getInputProps({
          onFocus,
          onBlur,
          ref,
          ...inputProps,
        })}
        placeholder={placeholder ?? ''}
        disabled={disabled}
        aria-label={placeholder}
        aria-invalid={error ? 'true' : 'false'}
        aria-errormessage={ERROR_MESSAGE_ID}
        $hasLeftIcon={LeftIcon != null}
        inputMode="text"
        enterKeyHint="done"
      />

      {/* ICONE A DROITE DE L'INPUT */}
      {isClearable ? (
        <ClearIcon id="autocomplete-clear" name="cross" size={24} onClick={onClear} />
      ) : RightIcon ? (
        RightIcon
      ) : null}

      {/* LISTE DE RESULTATS */}
      <Menu {...getMenuProps()} $hasResults={items.length > 0 && isMenuOpen}>
        {value.length === 0 || error || !isMenuOpen ? null : loading && items.length === 0 ? (
          <Loading>
            {I18n.t('common.searching-for', {
              search: value,
            })}
          </Loading>
        ) : (
          <>
            {items.map((item, index) => (
              <div
                key={`autocomplete-${index}`}
                {...getItemProps({
                  item,
                  index,
                })}>
                {renderItem(item, index === highlightedIndex)}
              </div>
            ))}
            {ResultFooter != null ? (
              <ResultFooterContainer $hasResult={items.length > 0}>
                {ResultFooter}
              </ResultFooterContainer>
            ) : null}
          </>
        )}
      </Menu>

      {/* MESSAGE D'ERREUR */}
      {value && error ? (
        <ErrorMessage id={ERROR_MESSAGE_ID} role="alert">
          {error}
        </ErrorMessage>
      ) : null}
    </Container>
  );
};

export default forwardRef(AutocompleteInput) as <T, HTMLInputElement>(
  props: Props<T> & { ref?: React.Ref<HTMLInputElement> },
) => ReturnType<typeof AutocompleteInput>;
